import React, { useState, useEffect } from "react";
import "./AddContentModal.css";
import VideoRecorder from "./VideoRecorder";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckSquare,
  faDotCircle,
  faPenFancy,
  faLink,
  faArrowsAltH,
  faSpellCheck,
  faFileAlt,
  faGamepad,
  faArrowLeft,
  faImage,
  faVideo,
  faVolumeUp,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { faYoutube } from "@fortawesome/free-brands-svg-icons";

const AddContentModal = ({ show, onClose, onSave, position, modalType }) => {
  const [showGames, setShowGames] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [image, setImage] = useState(null);
  const [video, setVideo] = useState(null);
  const [youtubeLink, setYoutubeLink] = useState("");
  const [caption, setCaption] = useState("");
  const [error, setError] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [videoPreview, setVideoPreview] = useState(null);

  // Reset state when the modal is opened
  useEffect(() => {
    if (show) {
      // Reset media-related state
      setSelectedMedia(null);
      setImage(null);
      setVideo(null);
      setYoutubeLink("");
      setCaption("");
      setError("");
      setIsRecording(false);
      setVideoPreview(null);

      // Reset question-related state if needed
      setShowGames(false);
    }
  }, [show, modalType]);

  // Clean up preview URL when component unmounts or when videoPreview changes
  useEffect(() => {
    return () => {
      if (videoPreview) {
        URL.revokeObjectURL(videoPreview);
      }
    };
  }, [videoPreview]);

  // Add this effect to clean up resources when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any resources when component unmounts
      if (videoPreview) {
        URL.revokeObjectURL(videoPreview);
      }
    };
  }, []);

  const questionTypes = [
    { type: "Multiple Choice", icon: faDotCircle },
    { type: "Multiple Checkbox", icon: faCheckSquare },
    { type: "Fill in the Blanks", icon: faPenFancy },
    { type: "Pairing", icon: faLink },
    { type: "Sentence Ordering", icon: faArrowsAltH },
    { type: "Spell the Word", icon: faSpellCheck },
    { type: "Open-Ended Text", icon: faFileAlt },
    { type: "Games", icon: faGamepad },
  ];

  const gameTypes = [
    { type: "Flashcards", icon: faGamepad },
    { type: "The Guessinator", icon: faGamepad },
    { type: "Lucky Spin", icon: faGamepad },
  ];

  if (!show) return null;

  const modalStyle = position
    ? {
        "--modal-top": `${position.top}px`,
      }
    : {};

  // Question-related handlers
  const handleTypeSelection = (type) => {
    if (type === "Games") {
      setShowGames(true);
    } else {
      onSave({
        type: "questions",
        questionType: type,
        question: "",
        options: [],
        correctAnswers: [],
        score: 1,
      });
      onClose();
    }
  };

  const handleGameSelection = (type) => {
    const gameData = {
      type: "questions",
      questionType: type,
      question: "",
      correctAnswers: [""],
      score: 1,
    };

    if (type === "Flashcards") {
      gameData.cards = [{ front: "", back: "" }];
    }

    onSave(gameData);
    onClose();
  };

  // Media-related handlers
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 8 * 1024 * 1024) {
      setError("Image size must be less than 8MB");
      return;
    }
    if (file && !["image/jpeg", "image/png", "image/gif"].includes(file.type)) {
      setError("Invalid image type. Allowed types are jpg, gif, and png.");
      return;
    }
    setError("");
    setImage(file);
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 15 * 1024 * 1024) {
      setError("Video size must be less than 15MB");
      return;
    }
    if (file && !["video/mp4", "video/mpeg4"].includes(file.type)) {
      setError("Invalid video type. Allowed types are mp4 and mpeg4.");
      return;
    }
    setError("");
    setVideo(file);
    setYoutubeLink("");
  };

  const handleSaveImage = () => {
    if (!image) {
      setError("Please select an image to upload");
      return;
    }
    onSave("image", { image, caption });
    onClose();
  };

  const handleSaveVideo = () => {
    if (!video && !youtubeLink) {
      setError(
        "Please select a video, paste a YouTube link, or record yourself"
      );
      return;
    }
    onSave("video", { video, youtubeLink, caption });
    onClose();
  };

  const handleRecordedVideo = (recordedVideo) => {
    setVideo(recordedVideo);
    setYoutubeLink("");
    setIsRecording(false);

    // Create a preview URL for the recorded video
    const previewUrl = URL.createObjectURL(recordedVideo);
    setVideoPreview(previewUrl);
  };

  const handleYoutubeLinkChange = (e) => {
    setYoutubeLink(e.target.value);
    if (e.target.value) {
      setVideo(null);
    }
  };

  const renderVideoUpload = () => (
    <>
      <h2>
        <button className="back-button" onClick={() => setSelectedMedia(null)}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        Upload Video
      </h2>
      {error && <p className="error-message">{error}</p>}

      {!isRecording ? (
        <>
          {/* Show video thumbnail if there's a video preview */}
          {videoPreview ? (
            <div className="recorded-video-preview">
              <div className="video-thumbnail-container">
                <video src={videoPreview} className="video-thumbnail" />
                <div className="video-info">
                  <h3>Recorded Video</h3>
                  <div className="thumbnail-actions">
                    <button
                      onClick={() => {
                        setVideo(null);
                        setVideoPreview(null);
                      }}
                      className="re-record-button"
                    >
                      Re-record
                    </button>
                  </div>
                </div>
              </div>

              <div className="caption-container">
                <input
                  type="text"
                  placeholder="Add a caption (optional)"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                />
                <div className="modal-buttons">
                  <button onClick={handleSaveVideo}>Save</button>
                  <button onClick={() => setSelectedMedia(null)}>Cancel</button>
                </div>
              </div>
            </div>
          ) : (
            // Show the regular upload options if no video is recorded yet
            <>
              <div className="image-segment">
                <div className="segment-icon">
                  <FontAwesomeIcon icon={faUpload} size="2x" />
                </div>
                <div className="segment-content">
                  <div className="button-text-container">
                    <label htmlFor="file-upload" className="custom-file-upload">
                      Choose File
                    </label>
                    <span className="info-text">
                      Maximum video size is 15MB (mp4 and mpeg4)
                    </span>
                  </div>
                  <input
                    id="file-upload"
                    type="file"
                    accept="video/mp4,video/mpeg4"
                    onChange={handleVideoChange}
                    style={{ display: "none" }}
                  />
                </div>
              </div>

              <div className="image-segment">
                <div className="segment-icon">
                  <FontAwesomeIcon icon={faVideo} size="2x" />
                </div>
                <div className="segment-content">
                  <div className="button-text-container">
                    <button
                      onClick={() => setIsRecording(true)}
                      className="record-button"
                    >
                      Record Yourself
                    </button>
                    <span className="info-text">
                      Maximum video length is 6 minutes
                    </span>
                  </div>
                </div>
              </div>

              <div className="image-segment">
                <div className="segment-icon">
                  <FontAwesomeIcon icon={faYoutube} size="2x" />
                </div>
                <div className="segment-content">
                  <input
                    type="text"
                    placeholder="Paste YouTube link"
                    value={youtubeLink}
                    onChange={handleYoutubeLinkChange}
                  />
                </div>
              </div>

              <div className="caption-container">
                <input
                  type="text"
                  placeholder="Add a caption (optional)"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                />
                <div className="modal-buttons">
                  <button onClick={handleSaveVideo}>Save</button>
                  <button onClick={() => setSelectedMedia(null)}>Cancel</button>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <VideoRecorder
          onRecordingComplete={handleRecordedVideo}
          onCancel={() => {
            setIsRecording(false);
            if (videoPreview) {
              URL.revokeObjectURL(videoPreview);
              setVideoPreview(null);
            }
          }}
        />
      )}
    </>
  );

  const renderImageUpload = () => (
    <>
      <h2>
        <button className="back-button" onClick={() => setSelectedMedia(null)}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        Upload Image
      </h2>
      {error && <p className="error-message">{error}</p>}
      <div className="image-segment">
        <div className="segment-icon">
          <FontAwesomeIcon icon={faUpload} size="2x" />
        </div>
        <div className="segment-content">
          <label htmlFor="image-upload" className="custom-file-upload">
            Choose File
          </label>
          <input
            id="image-upload"
            type="file"
            accept="image/jpeg,image/png,image/gif"
            onChange={handleImageChange}
            style={{ display: "none" }}
          />
          <p>Maximum image size is 8MB (jpg, png, and gif).</p>
        </div>
      </div>

      <div className="caption-container">
        <input
          type="text"
          placeholder="Add a caption (optional)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />
        <div className="modal-buttons">
          <button onClick={handleSaveImage}>Save</button>
          <button onClick={() => setSelectedMedia(null)}>Cancel</button>
        </div>
      </div>
    </>
  );

  const renderMediaOptions = () => (
    <>
      <h2>Add Media</h2>
      <div className="media-options">
        <button
          className="media-button"
          onClick={() => setSelectedMedia("image")}
        >
          <FontAwesomeIcon icon={faImage} size="2x" />
          <span>Add Image</span>
        </button>

        <button
          className="media-button"
          onClick={() => setSelectedMedia("video")}
        >
          <FontAwesomeIcon icon={faVideo} size="2x" />
          <span>Add Video</span>
        </button>

        <button className="media-button" onClick={() => onSave("audio")}>
          <FontAwesomeIcon icon={faVolumeUp} size="2x" />
          <span>Add Audio</span>
        </button>
      </div>
    </>
  );

  const renderQuestionsOptions = () => (
    <>
      <h2>
        {showGames && (
          <button className="back-button" onClick={() => setShowGames(false)}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        )}
        {showGames ? "Select Game Type" : "Select Question Type"}
      </h2>
      <div className="question-type-buttons">
        {showGames
          ? gameTypes.map(({ type, icon }) => (
              <button
                key={type}
                className="question-type-button games-button"
                onClick={() => handleGameSelection(type)}
              >
                <FontAwesomeIcon icon={icon} /> {type}
              </button>
            ))
          : questionTypes.map(({ type, icon }) => (
              <button
                key={type}
                className={`question-type-button ${
                  type === "Games" ? "games-button" : ""
                }`}
                onClick={() => handleTypeSelection(type)}
              >
                <FontAwesomeIcon icon={icon} /> {type}
              </button>
            ))}
      </div>
    </>
  );

  // Determine content based on modalType
  const renderContent = () => {
    if (modalType === "media") {
      if (selectedMedia === "image") return renderImageUpload();
      if (selectedMedia === "video") return renderVideoUpload();
      return renderMediaOptions();
    } else if (modalType === "questions") {
      return renderQuestionsOptions();
    }
    return null;
  };

  return (
    <div className="add-content-modal-overlay">
      <div className="add-content-modal-content" style={modalStyle}>
        <button className="close-button" onClick={onClose}>
          ×
        </button>
        {renderContent()}
      </div>
    </div>
  );
};

export default AddContentModal;
